import { useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calculator, Palette, Clock, Wrench } from "lucide-react";

interface PricingConfig {
  services: Record<string, number>;
  styles: Record<string, number>;
  timeline: Record<string, number>;
}

const PRICING: PricingConfig = {
  services: {
    'frame-painting': 800,
    'hubs': 300,
    'spokes': 200,
    'wheels': 600,
    'saddle': 250,
    'handlebars': 350,
    'accessories': 400
  },
  styles: {
    'dark': 1.0,
    'neon': 1.8,
    'classic': 0.7
  },
  timeline: {
    '1-month': 1.5,
    '2-months': 1.0,
    '3-months': 0.8
  }
};

export const TuningCalculator = () => {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedStyle, setSelectedStyle] = useState<string>('classic');
  const [selectedTimeline, setSelectedTimeline] = useState<string>('2-months');

  const handleServiceChange = useCallback((serviceId: string, checked: boolean) => {
    setSelectedServices(prev => 
      checked 
        ? [...prev, serviceId]
        : prev.filter(id => id !== serviceId)
    );
  }, []);

  const calculatePrice = useCallback(() => {
    const basePrice = selectedServices.reduce((total, serviceId) => {
      return total + PRICING.services[serviceId];
    }, 0);
    
    const styleMultiplier = PRICING.styles[selectedStyle];
    const timelineMultiplier = PRICING.timeline[selectedTimeline];
    
    return Math.round(basePrice * styleMultiplier * timelineMultiplier);
  }, [selectedServices, selectedStyle, selectedTimeline]);

  const services = [
    { id: 'frame-painting', label: 'Malowanie ramy', icon: Palette },
    { id: 'hubs', label: 'Piasty', icon: Wrench },
    { id: 'spokes', label: 'Szprychy', icon: Wrench },
    { id: 'wheels', label: 'Koła (całość)', icon: Wrench },
    { id: 'saddle', label: 'Siodełko', icon: Wrench },
    { id: 'handlebars', label: 'Kierownica', icon: Wrench },
    { id: 'accessories', label: 'Dodatki i akcesoria', icon: Wrench }
  ];

  const styles = [
    { 
      id: 'classic', 
      name: 'Klasyczny', 
      description: 'Subtelne modyfikacje, odświeżenie z zachowaniem oryginalnego stylu',
      price: 'Najtańsza opcja'
    },
    { 
      id: 'dark', 
      name: 'Mroczny', 
      description: 'Tuning na czarne, matowe motywy, minimalizm i elegancja. Inspiracje z batmobilu',
      price: 'Średnia cena'
    },
    { 
      id: 'neon', 
      name: 'Neon', 
      description: 'Najbardziej oczojebna przeróbka, gradienty, jaskrawe kolory. Maksymalna widoczność',
      price: 'Najdroższa opcja'
    }
  ];

  const timelines = [
    { id: '3-months', label: '3 miesiące', multiplier: 'Najniższa cena' },
    { id: '2-months', label: '2 miesiące', multiplier: 'Średnia cena' },
    { id: '1-month', label: '1 miesiąc', multiplier: 'Najwyższa cena' }
  ];

  const estimatedPrice = calculatePrice();

  return (
    <section id="calculator" className="py-24 px-6 bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-6 bg-white/90 px-6 py-4 rounded-full shadow-sm">
            <div className="p-2 bg-amber-400/20 rounded-full">
              <Calculator className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="font-montserrat font-bold text-4xl md:text-5xl">
              Kalkulator <span className="text-amber-600">Tuningu</span>
            </h2>
          </div>
          <p className="text-xl text-muted-foreground font-inter">
            Sprawdź orientacyjną cenę dla Twojego projektu
          </p>
        </div>

        <Card className="p-8 bg-white/90 backdrop-blur-sm border-amber-200/50 shadow-lg">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Services Selection */}
            <div>
              <h3 className="font-montserrat font-bold text-2xl mb-6 flex items-center gap-2">
                <Wrench className="w-6 h-6 text-primary" />
                Wybierz usługi
              </h3>
              <div className="space-y-4">
                {services.map((service) => (
                  <div key={service.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent/20 transition-colors">
                    <Checkbox
                      id={service.id}
                      checked={selectedServices.includes(service.id)}
                      onCheckedChange={(checked) => handleServiceChange(service.id, checked as boolean)}
                      className="border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <service.icon className="w-5 h-5 text-muted-foreground" />
                    <Label htmlFor={service.id} className="font-inter cursor-pointer flex-1">
                      {service.label}
                    </Label>
                    <span className="text-sm text-muted-foreground font-mono">
                      {PRICING.services[service.id]} zł
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Style and Timeline Selection */}
            <div className="space-y-8">
              {/* Style Selection */}
              <div>
                <h3 className="font-montserrat font-bold text-2xl mb-6 flex items-center gap-2">
                  <Palette className="w-6 h-6 text-secondary" />
                  Styl tuningu
                </h3>
                <RadioGroup value={selectedStyle} onValueChange={setSelectedStyle} className="space-y-4">
                  {styles.map((style) => (
                    <div key={style.id} className="flex items-start space-x-3 p-4 rounded-lg border border-accent/20 hover:border-primary/30 transition-colors">
                      <RadioGroupItem 
                        value={style.id} 
                        id={style.id}
                        className="border-primary text-primary mt-1"
                      />
                      <div className="flex-1">
                        <Label htmlFor={style.id} className="font-montserrat font-semibold text-lg cursor-pointer">
                          {style.name}
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1 font-inter">
                          {style.description}
                        </p>
                        <span className="text-xs text-secondary font-semibold mt-2 block">
                          {style.price}
                        </span>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Timeline Selection */}
              <div>
                <h3 className="font-montserrat font-bold text-2xl mb-6 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-warning" />
                  Czas realizacji
                </h3>
                <RadioGroup value={selectedTimeline} onValueChange={setSelectedTimeline} className="space-y-3">
                  {timelines.map((timeline) => (
                    <div key={timeline.id} className="flex items-center justify-between p-3 rounded-lg border border-accent/20 hover:border-warning/30 transition-colors">
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem 
                          value={timeline.id} 
                          id={timeline.id}
                          className="border-warning text-warning"
                        />
                        <Label htmlFor={timeline.id} className="font-inter font-medium cursor-pointer">
                          {timeline.label}
                        </Label>
                      </div>
                      <span className="text-xs text-warning font-semibold">
                        {timeline.multiplier}
                      </span>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* Price Display */}
          <div className="mt-8 pt-8 border-t border-accent/20">
            <div className="text-center">
              {estimatedPrice > 0 ? (
                <div className="bg-gradient-primary p-6 rounded-2xl neon-glow-cyan">
                  <p className="text-primary-foreground font-inter mb-2">Orientacyjna cena tuningu:</p>
                  <p className="font-montserrat font-black text-4xl text-primary-foreground">
                    {estimatedPrice.toLocaleString('pl-PL')} zł
                  </p>
                  <p className="text-primary-foreground/80 text-sm mt-2 font-inter">
                    *Cena może się różnić w zależności od szczegółów projektu
                  </p>
                </div>
              ) : (
                <div className="text-muted-foreground py-8">
                  <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-inter">Wybierz usługi, aby zobaczyć wycenę</p>
                </div>
              )}
            </div>
          </div>

          {estimatedPrice > 0 && (
            <div className="mt-6 text-center">
              <Button variant="heroSecondary" size="lg" className="interactive-scale">
                Wyślij zapytanie o wycenę
              </Button>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
};